import { makeStyles } from '@material-ui/core';
import { Entity } from '@backstage/catalog-model';
import { useByxTableStyles } from './table';
import { ByxT, useByxT } from './translations';
import { byxScheme } from '../theme/tokens';

/**
 * Cells shared by the Catálogo table and the entity pages (Dependências):
 * the same name, type and lifecycle rules in every list.
 */

const useStyles = makeStyles(theme => {
  const scheme = byxScheme(theme.palette.type);
  return {
    lifecycle: {
      color: theme.palette.text.primary,
    },
    // Only the states that need attention get a color, in tones readable as
    // small text on each theme's background.
    experimental: {
      color: scheme.warningInk,
    },
    deprecated: {
      color: scheme.error,
    },
  };
});

/**
 * The name shown for an entity: its display name or title, else its
 * `metadata.name` — never `kind:name`.
 */
export function entityTitle(entity: Entity): string {
  const profile = entity.spec?.profile as { displayName?: unknown } | undefined;
  for (const value of [profile?.displayName, entity.metadata.title]) {
    if (typeof value === 'string' && value) {
      return value;
    }
  }
  return entity.metadata.name;
}

/** pt-BR label of a lifecycle; unknown values are shown as they are. */
export function lifecycleLabel(t: ByxT, lifecycle: string): string {
  switch (lifecycle) {
    case 'production':
      return t('catalog.lifecycle.production');
    case 'experimental':
      return t('catalog.lifecycle.experimental');
    case 'deprecated':
      return t('catalog.lifecycle.deprecated');
    default:
      return lifecycle;
  }
}

/** The entity's lifecycle when it is a non-empty string. */
export function entityLifecycle(entity: Entity): string | undefined {
  // spec.lifecycle is any JSON value in the entity type; only a string is a
  // lifecycle we can show.
  const lifecycle = entity.spec?.lifecycle;
  return typeof lifecycle === 'string' && lifecycle ? lifecycle : undefined;
}

/** The entity's `spec.type` when it is a non-empty string. */
export function entityType(entity: Entity): string | undefined {
  const type = entity.spec?.type;
  return typeof type === 'string' && type ? type : undefined;
}

// Plain text as in the design; experimental and deprecated get a color.
export function LifecycleCell(props: Readonly<{ entity: Entity }>) {
  const table = useByxTableStyles();
  const classes = useStyles();
  const { t } = useByxT();
  const lifecycle = entityLifecycle(props.entity);
  if (!lifecycle) {
    return null;
  }
  let tone = classes.lifecycle;
  if (lifecycle === 'experimental') {
    tone = classes.experimental;
  } else if (lifecycle === 'deprecated') {
    tone = classes.deprecated;
  }
  const label = lifecycleLabel(t, lifecycle);
  return (
    <span className={`${table.cell} ${table.lifecycle} ${tone}`} title={label}>
      {label}
    </span>
  );
}

export function TypeCell(
  props: Readonly<{ entity: Entity; narrow?: boolean }>,
) {
  const { entity, narrow } = props;
  const table = useByxTableStyles();
  const type = entityType(entity);
  if (!type) {
    return null;
  }
  return (
    <span
      className={`${table.cell} ${narrow ? table.apiType : table.type}`}
      title={type}
    >
      {type}
    </span>
  );
}
